import { type ReactElement } from 'react';
import { ProjectBadge } from '@/features/projects/components/ProjectBadge';
import { s } from '@/theme/tokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { Box, Text } from '@/ui/primitives';
import type { ProjectDto } from '@flowqa/shared';

type TableV2ProjectCellProps = {
  project: Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'> | null;
  fallbackName?: string | null;
};

export const TableV2ProjectCell = ({
  fallbackName = null,
  project,
}: TableV2ProjectCellProps): ReactElement => {
  const projectName = project?.name ?? fallbackName ?? '-';
  const projectBadge =
    project?.badgeEnabled === true ? (
      <ProjectBadge chars={project.badgeChars} color={project.badgeColor} />
    ) : null;
  const tooltipTitle = (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        display: 'inline-flex',
        gap: `calc((${s('sm')} + ${s('md')}) / 2)`,
      }}
    >
      {projectBadge}
      <Text role="body" component="span" sx={{ fontSize: '16px !important' }}>
        {projectName}
      </Text>
    </Box>
  );

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: `calc((${s('sm')} + ${s('md')}) / 2)`,
        minWidth: 0,
      }}
    >
      {projectBadge}
      <OverflowTooltip title={tooltipTitle} wrapperSx={{ minWidth: 0 }}>
        <Text
          role="body"
          color="textSecondary"
          component="span"
          sx={{
            display: 'block',
            fontSize: '16px !important',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {projectName}
        </Text>
      </OverflowTooltip>
    </Box>
  );
};
